import { Injectable } from '@angular/core';
import { ethers } from 'ethers';
import { WALLET_LOGIN } from '../common/interface/common.interface';
import { AuthService } from './auth.service';
import { STORAGE_KEYS, WALLET_PROVIDERS } from '../common/constants/common.constants';
import { createPublicClient, http, formatUnits } from 'viem';
import { polygonAmoy } from 'viem/chains';
import { environment } from '../../environments/environment';
import { readContract } from '@wagmi/core';
import { ToastService } from './toast.service';
import { AppKitService } from './app-kit.service';


@Injectable({
  providedIn: 'root'
})
export class Web3ServiceService {
  private provider: ethers.BrowserProvider | null = null;
  private readProvider = new ethers.JsonRpcProvider(
    environment.PROVIDERS.polygonAmoy
  );
  private readonly CTF_EXCHANGE_ADDRESS = environment.CTF_EXCHANGE_ADDRESS;

  constructor(
    private toastr: ToastService,
    private appKitService: AppKitService
  ) { }

  init(providerOption: WALLET_LOGIN | null = null): ethers.BrowserProvider | null {
    if (!providerOption) {
      let option: string | null = localStorage.getItem(STORAGE_KEYS.LOGIN_TYPE);
      providerOption = option ? JSON.parse(option).provider : option;
    }

    const injected = this.getInjectedProvider(providerOption);
    if (injected)
      this.provider = new ethers.BrowserProvider(injected);

    return this.provider
  }

  /**
   * Picks the injected provider matching the login option. With several wallet
   * extensions installed, window.ethereum is not necessarily MetaMask, so the
   * `providers` list is searched before falling back to window.ethereum.
   */
  private getInjectedProvider(providerOption: WALLET_LOGIN | string | null): any | null {
    const injected = (window as any).ethereum;
    if (!injected) return null;

    if (providerOption && providerOption != WALLET_PROVIDERS.METAMASK)
      return null;

    const candidates: any[] = Array.isArray(injected.providers) ? injected.providers : [injected];
    return candidates.find((candidate: any) => candidate?.isMetaMask) ?? null;
  }

  /**
   * Reconnects the wallet session (e.g. after a hard refresh) and returns a
   * provider bound to the connector that is actually in use.
   */
  async reconnect(): Promise<ethers.BrowserProvider | null> {
    const walletProvider = await this.appKitService.getConnectedProvider();

    this.provider = walletProvider
      ? new ethers.BrowserProvider(walletProvider)
      : this.init();

    return this.provider;
  }

  async connectWallet(): Promise<string | null> {
    if (!this.provider)
      await this.reconnect();

    let accounts = await this.provider?.send("eth_requestAccounts", []);
    return accounts;
  }

  async getCurrentAccount(): Promise<string | null> {
    if (!this.provider)
      await this.reconnect();

    const accounts = await this.provider?.send("eth_accounts", []);
    return accounts?.length > 0 ? accounts[0] : null;
  }

  async getSignerAndAddress(): Promise<any> {
    // Always refresh the provider to avoid stale network state after chain switches
    await this.refreshProvider();

    const provider = this.provider as ethers.BrowserProvider | null;
    if (!provider)
      return { signer: undefined, address: undefined };

    try {
      // Prefer the account wagmi is connected with, so the signer never points
      // at a different account the wallet happens to list first
      const connectedAddress: string | undefined = this.appKitService.getCurrentWalletState()?.address;
      const signer: ethers.JsonRpcSigner = connectedAddress
        ? await provider.getSigner(connectedAddress)
        : await provider.getSigner();
      const address: string = await signer.getAddress();
      return { signer, address };
    } catch (error) {
      console.error('Unable to resolve signer:', error);
      return { signer: undefined, address: undefined };
    }
  }

  async getAccountBalance(): Promise<string | number | null> {
    if (!this.provider)
      await this.reconnect();

    let { address } = await this.getSignerAndAddress();
    const balanceWei: ethers.BigNumberish | undefined = await this.provider?.getBalance(address);
    let balance = "0";
    if (balanceWei) balance = ethers.formatEther(balanceWei);
    return balance
  }

  async createSignature(payload: { message: string; nonce: string; }): Promise<any> {
    if (!this.provider)
      await this.reconnect();

    let { signer, address } = await this.getSignerAndAddress();
    return await signer?.signMessage(payload.message)
  }

  clearState(): void {
    this.provider = null;
  }

  /**
   * Force-recreate the BrowserProvider and wait for it to detect the current network.
   * This prevents NETWORK_ERROR / BAD_DATA after the user switches chains and switches back.
   */
  private async refreshProvider(): Promise<void> {
    this.provider = null;
    // Rebuild from the live connector, restoring the session first when needed
    const provider = await this.reconnect();
    if (!provider) return;

    // Force the provider to query and cache the CURRENT network
    // Without this, ethers may still think it's on the old chain
    try {
      const network = await provider.getNetwork();
      const chainId = Number(network.chainId);

      // If not on Amoy, wait briefly and retry once — MetaMask may still be settling
      if (chainId !== 80002) {
        await new Promise(resolve => setTimeout(resolve, 500));
        this.provider = null;
        const retryProvider = await this.reconnect();
        if (retryProvider) {
          await retryProvider.getNetwork();
        }
      }
    } catch (e) {
      console.warn('Network detection during provider refresh:', e);
    }
  }

  client = createPublicClient({
    chain: polygonAmoy,
    transport: http('https://polygon-amoy.drpc.org'),
  });


  erc20Abi = [
    {
      name: 'balanceOf',
      type: 'function',
      stateMutability: 'view',
      inputs: [{ name: 'account', type: 'address' }],
      outputs: [{ type: 'uint256' }],
    },
    {
      name: 'decimals',
      type: 'function',
      stateMutability: 'view',
      inputs: [],
      outputs: [{ type: 'uint8' }],
    },

    {
      name: 'transfer',
      type: 'function',
      stateMutability: 'nonpayable',
      inputs: [
        { name: 'to', type: 'address' },
        { name: 'value', type: 'uint256' }
      ],
      outputs: [{ type: 'bool' }],
    },

  ];

  async getUSDCBalance(address: `0x${string}`) {
    try {
      // get decimals (USDC = usually 6)
      const decimals: any = await this.client.readContract({
        address: environment.USDC_ADDRESS as `0x${string}`,
        abi: this.erc20Abi,
        functionName: 'decimals',
      });

      // get raw balance
      const balance: any = await this.client.readContract({
        address: environment.USDC_ADDRESS as `0x${string}`,
        abi: this.erc20Abi,
        functionName: 'balanceOf',
        args: [address],
      });

      return formatUnits(balance, decimals);
    } catch (error) {
      console.error('USDC balance error:', error);
      return null;
    }
  }

  async transferUSDC(to: string, amount: number) {
    try {
      const { signer, address } = await this.getSignerAndAddress();
      if (!signer || !address)
        throw new Error("Wallet not connected. Please reconnect your wallet and try again.");

      // Verify we are on Polygon Amoy before making any contract calls
      const currentProvider = this.provider as ethers.BrowserProvider | null;
      if (currentProvider) {
        const network = await currentProvider.getNetwork();
        const chainId = Number(network.chainId);
        if (chainId !== 80002) {
          throw new Error(`Wrong network. Please switch to Polygon Amoy.`);
        }
      }

      const readContract = new ethers.Contract(
        environment.USDC_ADDRESS,
        this.erc20Abi,
        this.readProvider
      );

      const writeContract = new ethers.Contract(
        environment.USDC_ADDRESS,
        this.erc20Abi,
        signer
      );
      // Read using RPC provider
      const decimals = await readContract['decimals']();

      const parsedAmount = ethers.parseUnits(
        amount.toString(),
        decimals
      );


      // Read balance using RPC provider
      const balance = await readContract['balanceOf'](
        ethers.getAddress(address)
      );


      //  Check balance
      // if (balance < parsedAmount) {
      //   this.toastr.error("Insufficient Balance");
      //   return;
      // }


      // Estimate gas
      let gasLimit;
      try {
        const estimatedGas = await writeContract['transfer'].estimateGas(
          to,
          parsedAmount
        ); gasLimit = (estimatedGas * 120n) / 100n; // Add 20% buffer
        // console.log("Estimated gas:", estimatedGas, "With Buffer:", gasLimit);
      } catch (e) {
        console.error("Gas estimation failed, using fallback:", e);
        gasLimit = 100000n; // Fallback gas limit
      }

      // Get gas price from network
      const feeData = await this.readProvider.getFeeData();

      const gasPrice = feeData.gasPrice;
      // Send transaction
      const tx = await writeContract['transfer'](to, parsedAmount, {
        gasLimit,
        gasPrice
      });

      return await tx.wait();

    } catch (error: any) {
      console.error('Transfer error:', error);
      throw error;
    }
  }

  async getCtfExchangeAbi(): Promise<any> {
    const abi = await import('../../assets/contract/CTFExchange_abi.json');
    return abi.default;
  }

  async getUserNonce(userAddress: string) {

    const chainId = await this.client.getChainId();
    // console.log("Connected Chain ID:", chainId);
    // console.log("CONTRACT:", this.CTF_EXCHANGE_ADDRESS);
    const abi = await this.getCtfExchangeAbi();

    const client = createPublicClient({
      chain: polygonAmoy,
      transport: http(environment.PROVIDERS.polygonAmoy)

    });

    const nonce = await client.readContract({
      address: this.CTF_EXCHANGE_ADDRESS as `0x${string}`,
      abi: abi,
      functionName: 'nonces',
      args: [userAddress],
    });

    return nonce;
  }
}




